import Store from 'repatch';

import State from '../core/State';
import { Role } from '../entity/MembershipRole';
import { PermissionAction } from '../entity/Organization';
import Organization from '../entity/Organization';
import Tag from '../entity/Tag';
import { finishSpinner, startSpinner } from '../reducer/fullScreenSpinner';
import {
  deleteMembership,
  setCurrentOrganizationInvitations,
  setCurrentOrganizationMemberships,
  setCurrentOrgOrAccount,
  setOrganizations,
  startFetchingOrganization,
  startLoadingOrganizationMemberships,
  updateMembership,
} from '../reducer/organization';
import ApiErrorHandler from '../service/ApiErrorHandler';
import MessageDispatcher from '../service/MessageDispatcher';
import OrganizationWebApi from '../service/persistent/OrganizationWebApi';

class OrganizationUsecase {
  private store: Store<State>;
  private organizationWebApi: OrganizationWebApi;
  private messageDispatcher: MessageDispatcher;
  private apiErrorHandler: ApiErrorHandler;

  constructor({
    store,
    organizationWebApi,
    messageDispatcher,
    apiErrorHandler,
  }: {
    store: Store<State>;
    organizationWebApi: OrganizationWebApi;
    messageDispatcher: MessageDispatcher;
    apiErrorHandler: ApiErrorHandler;
  }) {
    this.store = store;
    this.organizationWebApi = organizationWebApi;
    this.messageDispatcher = messageDispatcher;
    this.apiErrorHandler = apiErrorHandler;
  }

  public getOrganizations = async (currentId: string | null): Promise<void> => {
    this.store.dispatch(startFetchingOrganization());
    let organizations = await this.organizationWebApi.getOrganizations([
      PermissionAction.ORGANIZATIONS_READ,
      PermissionAction.ORGANIZATIONS_WRITE,
      PermissionAction.MEMBERSHIPS_READ,
      PermissionAction.MEMBERSHIPS_WRITE,
    ]);

    organizations = this.sortOrganizations(organizations);

    let current = null;
    if (organizations.length > 0) {
      if (currentId) {
        current = organizations.find(org => {
          return org.id == currentId;
        });
      }
      if (!current) {
        current = organizations[0];
      }
    }
    this.store.dispatch(setOrganizations(this.sortOrganizations(organizations), current));
  };

  public setCurrentOrgOrAccount = async (organization: Organization): Promise<void> => {
    this.store.dispatch(setCurrentOrgOrAccount(organization));
    window.newrelic.setCustomAttribute('org_id', organization.id);
  };

  public uploadAvatarImage = async (organizationId: string, files: File[] | FileList): Promise<void> => {
    const fileType = 'image/'; // Currently allow only thumbnail image
    try {
      this.store.dispatch(startSpinner());
      for (let i = 0; i < files.length; i++) {
        if (!files[i].type.startsWith(fileType)) {
          throw new Error(`message.uploading_file_must_be_image`);
        }
      }
      await this.organizationWebApi.uploadAvatarImage(organizationId, files[0]);
      await this.reloadCurrentOrganizations(organizationId);
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public updateGeneral = async ({
    id,
    name,
    comment,
    tags,
  }: {
    id: string;
    name: string;
    comment?: string;
    tags: Tag[];
  }): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.organizationWebApi.updateGeneral({ id, name, comment, tags });
      await this.reloadCurrentOrganizations(id);
      this.messageDispatcher.notifyUnimportant('organization_setting.general.settings_updated');
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public createInvitation = async ({
    id,
    email,
    roles,
  }: {
    id: string;
    email: string;
    roles: Role[];
  }): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.organizationWebApi.createInvitation({ id, email, roles });
      await this.reloadCurrentOrganizationInvitations();
      this.messageDispatcher.notifyUnimportant('organization_setting.memberships.invitation_sent');
      return Promise.resolve();
    } catch (_err) {
      const err: any = _err;
      if (err.response.data.code === 'kaizen_id/email_already_exists_error') {
        return this.createMembership({ id, email, roles });
      }
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public createMembership = async ({
    id,
    email,
    roles,
  }: {
    id: string;
    email: string;
    roles: Role[];
  }): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.organizationWebApi.createMembership({ id, email, roles });
      await this.getMemberships(id);
      this.messageDispatcher.notifyUnimportant('organization_setting.memberships.membership_added');
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public getMemberships = async (organizationId: string): Promise<void> => {
    this.store.dispatch(startLoadingOrganizationMemberships());
    const memberships = await this.organizationWebApi.getMemberships(organizationId);
    this.store.dispatch(setCurrentOrganizationMemberships(organizationId, memberships));
  };

  public deleteMembership = async (id: string): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.organizationWebApi.deleteMembership(id);
      this.store.dispatch(deleteMembership(id));
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public createMembershipRoleAssignment = async (membershipId: string, role: Role): Promise<void> => {
    try {
      await this.organizationWebApi.createMembershipRoleAssignment(membershipId, role);
      await this.updateMembership(membershipId);
      this.messageDispatcher.notifyUnimportant('organization_setting.memberships.change_role');
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    }
  };

  public deleteMembershipRoleAssignment = async (membershipId: string, id: string): Promise<void> => {
    try {
      await this.organizationWebApi.deleteMembershipRoleAssignment(id);
      await this.updateMembership(membershipId);
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    }
  };

  public getInvitations = async (organizationId: string): Promise<void> => {
    const invitations = await this.organizationWebApi.getInvitations(organizationId);
    this.store.dispatch(setCurrentOrganizationInvitations(invitations));
  };

  public deleteInvitation = async (id: string): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.organizationWebApi.deleteInvitation(id);
      await this.reloadCurrentOrganizationInvitations();
      this.messageDispatcher.notifyUnimportant('organization_setting.memberships.invitation_canceled');
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public reloadCurrentOrganizations = async (currentOrganizationId: string | null): Promise<void> => {
    const state = this.store.getState();
    if (state.authentication.signedInUser) {
      await this.getOrganizations(currentOrganizationId);
    }
  };

  private updateMembership = async (id: string): Promise<void> => {
    const membership = await this.organizationWebApi.getMembership(id);
    this.store.dispatch(updateMembership(membership));
  };

  private reloadCurrentOrganizationInvitations = async (): Promise<void> => {
    const state = this.store.getState();
    if (state.currentOrgOrAccount) {
      await this.getInvitations(state.currentOrgOrAccount.id);
    }
  };

  private sortOrganizations = (organizations: Organization[]) => {
    return organizations.slice().sort((a, b) => {
      if (!b.isChildren && a.appVersion < b.appVersion) return 1;
      else if (!a.isChildren && a.appVersion > b.appVersion) return -1;
      else {
        if (Number(a.id) > Number(b.id)) return 1;
        else if (Number(a.id) < Number(b.id)) return -1;
        else return 0;
      }
    });
  };
}

export default OrganizationUsecase;
